import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Shield, 
  Users, 
  Globe, 
  Plane, 
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const Solutions = () => {
  const { t } = useTranslation('public');

  const solutions = [
    {
      icon: Shield,
      title: t('solutions.individuals.title'),
      subtitle: t('solutions.individuals.subtitle'),
      description: t('solutions.individuals.description'),
      features: [t('solutions.individuals.auto.title'), t('solutions.individuals.home.title'), t('solutions.individuals.health.title'), t('solutions.individuals.life.title')],
      link: '/solutions/particuliers',
      color: 'bg-blue-500'
    },
    {
      icon: Users,
      title: t('solutions.professionals.title'),
      subtitle: t('solutions.professionals.subtitle'),
      description: t('solutions.professionals.description'),
      features: [t('solutions.professionals.rcPro.title'), t('solutions.professionals.multirisque.title'), t('solutions.professionals.fleet.title'), t('solutions.professionals.healthPro.title')],
      link: '/solutions/professionnels',
      color: 'bg-green-500'
    },
    {
      icon: Globe,
      title: t('solutions.expatriates.title'),
      subtitle: t('solutions.expatriates.subtitle'),
      description: t('solutions.expatriates.description'),
      features: [t('solutions.expatriates.internationalHealth.title'), t('solutions.expatriates.expatLife.title'), t('solutions.expatriates.assistance.title'), t('solutions.expatriates.familyPack.title')],
      link: '/solutions/expatries',
      color: 'bg-purple-500'
    },
    {
      icon: Plane,
      title: t('solutions.travel.title'),
      subtitle: t('solutions.travel.subtitle'),
      description: t('solutions.travel.description'),
      features: [t('solutions.travel.cancellation.title'), t('solutions.travel.luggage.title'), t('solutions.travel.assistanceTravel.title'), t('solutions.travel.liability.title')],
      link: '/solutions/voyage',
      color: 'bg-orange-500'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brand-primary-mid to-brand-text-light text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t('solutions.title')}
            </h1>
            <p className="text-xl max-w-3xl mx-auto text-gray-100">
              {t('solutions.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const IconComponent = solution.icon;
              return (
                <div key={index} className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300 overflow-hidden">
                  <div className="p-8">
                    <div className="flex items-center mb-6">
                      <div className={`w-16 h-16 ${solution.color} rounded-xl flex items-center justify-center mr-4`}>
                        <IconComponent className="text-white" size={32} />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">{solution.title}</h2>
                        <p className="text-brand-primary-mid font-semibold">{solution.subtitle}</p>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                      {solution.description}
                    </p>
                    
                    <div className="space-y-3 mb-8">
                      {solution.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center">
                          <CheckCircle className="text-brand-primary-mid mr-3" size={18} />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <Link 
                      to={solution.link}
                      className="inline-flex items-center bg-gradient-to-r from-brand-primary-mid to-brand-text-light text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                    >
                      {t('solutions.discoverOffers')}
                      <ArrowRight className="ml-2" size={20} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Our Solutions */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('solutions.whyChoose')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('solutions.whyChooseSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-brand-primary-mid rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t('solutions.personalizedAnalysis.title')}
              </h3>
              <p className="text-gray-600">
                {t('solutions.personalizedAnalysis.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-brand-primary-mid rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t('solutions.expertSupport.title')}
              </h3>
              <p className="text-gray-600">
                {t('solutions.expertSupport.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-brand-primary-mid rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t('solutions.optimalGuarantees.title')}
              </h3>
              <p className="text-gray-600">
                {t('solutions.optimalGuarantees.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-text-light text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            {t('solutions.customSolution')}
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            {t('solutions.customSolutionSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/rendez-vous"
              className="bg-brand-primary-mid text-white px-8 py-4 rounded-lg font-semibold hover:bg-opacity-90 transition-colors"
            >
              {t('common.makeAppointment')}
            </Link>
            <Link 
              to="/contact"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-brand-text-light transition-colors"
            >
              {t('solutions.requestQuote')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;